
import './CoverImageContainer.scss';

import React, { ChangeEvent, FC, useContext } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { AppContext, NotificationType } from 'components/AppContext/AppContext';
import { useImageUploader } from 'customHooks/useImageUploader';
import { HttpMethod } from 'models/HttpMethod';
import { LogLevel } from 'models/LogLevel';
import { Form } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';

import { FileInfo } from '../../../models/FileInfo';


interface CoverImageContainerProps {
  // onImageUploading: () => void;
  bannerImage: FileInfo | undefined;
  setBannerImage: (file: FileInfo | undefined) => void;
  tabIndex: number | undefined;
  documentId: number | undefined;
}

const CoverImageContainer: FC<CoverImageContainerProps> = ({
  bannerImage,
  setBannerImage,
  tabIndex,
  documentId,
}) => {
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const { configRepo, selectedProjectId, notify, sendLog } = useContext(AppContext);
  const { handleCoverImageUpload } = useImageUploader(configRepo!.SCHEDULE_API_BASE_URI);

  const onImageChange = async (event: ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    const fileList = event.target.files;
    await handleCoverImageUpload(fileList, selectedProjectId!, setBannerImage);
  };

  const deleteImageFromAttachmentService = async (fileName: string, token: string) => {
    const settings: RequestInit = {
      method: HttpMethod.DELETE,
      mode: 'cors',
      cache: 'no-cache',
      credentials: 'include',
      body: JSON.stringify([fileName]),
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    };
    const result = await fetch(
      `${configRepo!.SCHEDULE_API_BASE_URI}/api/Attachments?ProjectId=${selectedProjectId}`,
      settings,
    );
    return result.ok;
  };

  const deleteImageFromDocument = async (token: string) => {
    if (!documentId) return true;

    const settings: RequestInit = {
      method: HttpMethod.DELETE,
      mode: 'cors',
      cache: 'no-cache',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    };
    const result = await fetch(
      `${configRepo!.SCHEDULE_API_BASE_URI}/api/Documents/${documentId}/BannerImage`,
      settings,
    );
    return result.ok;
  };

  const onDeleteImage = async (fileName: string) => {
    if (!fileName) return;
    try {
      const token = await getAccessTokenSilently();
      if (!token) {
        navigate('/');
      } else {
        const isImageDeleted = await deleteImageFromAttachmentService(fileName, token);
        if (isImageDeleted) {
          const isRemovedFromDocument = await deleteImageFromDocument(token);
          if (isRemovedFromDocument) {
            setBannerImage(undefined);
          }
        }
      }
    } catch (e: any) {
      const message = 'Unable to delete image.'
      notify(message, NotificationType.Error);
      sendLog(message, LogLevel.error, e);
    }
  };

  if (bannerImage) {
    return (
      <div className="image-label-container cover-image__label">
        <div className="overlay">
          <button
            type="button"
            onClick={() => {
              onDeleteImage(bannerImage.name);
            }}
            className="btn btn-danger delete-icon"
            title="Delete cover image"
            aria-label="Delete cover image"
          >
            <i className="bi bi-trash" />
          </button>
        </div>

        <img src={bannerImage.url} alt={bannerImage.name} className="img-fluid" />
      </div>
    );
  }

  return (
    <Form>
      <Form.Group className="mb-3 col-md-12" controlId="image_upload">
        <Form.Label className="image-label-container empty">
          <i className="bi bi-camera" />
          <p className="t-24">No cover image uploaded</p>
          <p className="t-14 mt2">Add a cover image to your article to attract more readers.</p>
          <p className="t-14 mt1">
            We recommend uploading an image with a pixel size of 1280 x 720
          </p>
        </Form.Label>
        <Form.Control
          type="file"
          onChange={(e: ChangeEvent<HTMLInputElement>) => onImageChange(e)}
          aria-label="image"
          name="image_upload"
          className="visually-hidden"
          tabIndex={tabIndex}
        />
      </Form.Group>
    </Form>
  );
};

export default CoverImageContainer;
